'use client';

import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import PickupPointSelectModal, {
  UpdateCollection,
} from '@/components/blocks/pickup-point/pickup-point-select-modal/pickup-point-select-modal';
import AlertPill from '@/components/molecules/alert-pill/alert-pill';
import Button from '@/components/molecules/button/button';
import Select, { SelectOption } from '@/components/molecules/select/select';
import Tag, { TagVariant } from '@/components/molecules/tag/tag';
import ExtraCardModal from '@/components/organisms/extra-card-modal/extra-card-modal';
import ImageSlider from '@/components/organisms/image-slider/image-slider';
import PriceItems from '@/components/organisms/price-items/price-items';
import SelectedPassengers from '@/components/organisms/selected-passengers/selected-passengers';
import TruncatedText from '@/components/organisms/truncated-text/truncated-text';
import Link from '@/components/utilities/link/link';
import ModuleWrapper from '@/components/utilities/module-wrapper/module-wrapper';
import { ModuleWrapperProps } from '@/components/utilities/module-wrapper/module-wrapper';
import useMutationAddPreBookingPickupPoint from '@/integrations/booking/pre-booking/pickup-point/add-pickup-point/add-pickup-point.hooks';
import useMutationDeletePreBookingPickupPoint from '@/integrations/booking/pre-booking/pickup-point/delete-pickup-point/delete-pickup-point.hooks';
import useQueryPickupPoint from '@/integrations/content/content-modules/pickup-point/pickup-point.hooks';
import { QueryId } from '@/integrations/content/utils';
import { useBookingFlowContext } from '@/libraries/contexts/booking-flow-context/booking-flow-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './pickup-point.module.scss';

export interface PickupPointProps extends ModuleWrapperProps {
  /** The UUID of the module */
  id: Content.Components['PickupPointHeadlessModuleDto']['id'];
  /** The UUID of the current page */
  pageId: Content.Components['PickupPointHeadlessModuleDto']['pageId'];
  /** The parameters for the booking */
  parameters: Content.Components['PickupPointHeadlessModuleDto']['parameters'];
  /** Module settings*/
  settings: Content.Components['PickupPointHeadlessModuleDto']['settings'];
}

export interface SingleOption {
  id: number;
  name: string;
  price: number;
}

const PickupPoint = ({
  id,
  pageId,
  parameters,
  settings,
}: PickupPointProps) => {
  const { t } = useI18n();
  const isLaptop = useWindowSize('laptop');
  const { brand } = usePageDetailsContext();
  const { dateFormatter } = useDateFormatter();
  const currencyFormatter = useCurrencyFormatter();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const {
    activeAnchor,
    anchorSignal,
    moduleStatusMap,
    setActiveAnchor,
    updateModuleStatus,
  } = useBookingFlowContext();

  const [pickupPoint, setPickupPoint] =
    useState<Content.Properties.PickupPoint>();
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [singleOption, setSingleOption] = useState<SingleOption | undefined>();
  const [multipleOptions, setMultipleOptions] = useState<SelectOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [selectedPassengers, setSelectedPassengers] = useState<
    Content.Components['PassengerDto'][]
  >([]);

  const identifiers = { ...parameters, id, pageId };
  const invalidationKeys = [QueryId.PICKUP_POINT];

  const {
    data: dataPickupPoint,
    isFetching: isFetchingPickupPoint,
    isLoading: isLoadingPickupPoint,
    refetch: refetchPickupPoint,
  } = useQueryPickupPoint({
    parameters: identifiers,
  });

  const hasNoData = !isFetchingPickupPoint && !dataPickupPoint;

  const {
    isLoading: isMutatingAddPreBookingPickupPoint,
    mutate: mutateAddPreBookingPickupPoint,
  } = useMutationAddPreBookingPickupPoint();

  const {
    isLoading: isMutatingDeletePreBookingPickupPoint,
    mutate: mutateDeletePreBookingPickupPoint,
  } = useMutationDeletePreBookingPickupPoint();

  const shouldHighlightErrors = useMemo(() => {
    return (
      activeAnchor === id &&
      !moduleStatusMap[id]?.isComplete &&
      moduleStatusMap[id]?.isRequired
    );
  }, [activeAnchor, id, moduleStatusMap]);

  const addPickupPoint = (uniquePaxHashes: string[], pickupPointId: string) => {
    mutateAddPreBookingPickupPoint({
      brand,
      data: {
        uniquePaxHash: uniquePaxHashes.join(','),
      },
      invalidationKeys: invalidationKeys,
      parameters: identifiers,
      pickupPointId: parseInt(pickupPointId),
    });
  };

  const deletePickupPoint = (uniquePaxHashes: string[]) => {
    mutateDeletePreBookingPickupPoint({
      brand,
      data: {
        uniquePaxHash: uniquePaxHashes.join(','),
      },
      invalidationKeys: invalidationKeys,
      parameters: identifiers,
    });
  };

  const handleAddForAllSingleSelect = () => {
    if (!pickupPoint) return;

    const relevantPickupPoint = pickupPoint.relevantPickupPoints[0];

    if (!relevantPickupPoint) return;

    const pickupPointId = relevantPickupPoint.pickupPointId.toString();
    const uniquePaxHashes = pickupPoint.passengers.map(
      passenger => passenger.uniquePaxHash,
    );

    addPickupPoint(uniquePaxHashes, pickupPointId);
  };

  const handleAddForAllMultipleSelect = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    if (!pickupPoint) return;

    setSelectedOption(event.target.value);

    const uniquePaxHashes = pickupPoint.passengers.map(
      passenger => passenger.uniquePaxHash,
    );

    if (event.target.value === 'none') {
      deletePickupPoint(uniquePaxHashes);

      return;
    }

    addPickupPoint(uniquePaxHashes, event.target.value);
  };

  const handleEdit = () => {
    setShowSelectModal(true);
  };

  const handleChooseIndividually = () => {
    setShowSelectModal(true);
  };

  const handleOnClickSave = (updateCollection: UpdateCollection) => {
    for (const selected of updateCollection.selected) {
      addPickupPoint(selected.uniquePaxHashes, selected.pickupPointId);
    }

    if (updateCollection.unselected.uniquePaxHashes.length > 0) {
      deletePickupPoint(updateCollection.unselected.uniquePaxHashes);
    }
  };

  useEffect(() => {
    if (!pickupPoint) return;

    const options: SelectOption[] = [
      ...(!dataPickupPoint?.unremovableOnPostBooking
        ? [
            {
              name: t('items.modules.bookingFlow.noneSelected'),
              value: 'none',
            },
          ]
        : []),
      ...pickupPoint.relevantPickupPoints.map(pickupPoint => ({
        name: `${pickupPoint.name} (${dateFormatter(pickupPoint.meetingHour).format('HH:mm')}) - ${currencyFormatter(pickupPoint.price)}`,
        value: pickupPoint.pickupPointId,
      })),
    ];

    setMultipleOptions(options);

    const selectedPassengers: Content.Components['PassengerDto'][] =
      pickupPoint.passengers.filter(
        passenger => !!passenger.selectedPickupPoint,
      );

    setSelectedPassengers(selectedPassengers);

    const relevantPickupPoint = pickupPoint.relevantPickupPoints[0];

    if (!relevantPickupPoint) return;

    const singleOption = {
      id: relevantPickupPoint.pickupPointId,
      name: relevantPickupPoint.name,
      price: relevantPickupPoint.price,
    };

    setSingleOption(singleOption);
    setSelectedOption(dataPickupPoint?.unremovableOnPostBooking ? '' : 'none');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupPoint]);

  useEffect(() => {
    if (!dataPickupPoint) return;

    setActiveAnchor(null);
    updateModuleStatus(id, {
      isComplete: dataPickupPoint.isComplete,
      isRequired: dataPickupPoint.isRequired,
    });
    setPickupPoint(dataPickupPoint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPickupPoint]);

  useEffect(() => {
    if (!componentRef.current) return;

    if (!activeAnchor) return;

    if (activeAnchor === id) {
      componentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [activeAnchor, anchorSignal, id]);

  useEffect(() => {
    refetchPickupPoint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoadingPickupPoint)
    return (
      <ModuleWrapper
        className={styles.component}
        id={id}
        padding="none"
        settings={settings}
      >
        <div className={styles.mainWrapper}>
          <div className={styles.topSection}>
            <SkeletonLoader
              className={styles.loader}
              height="25px"
              width="35%"
            />

            <SkeletonLoader
              className={styles.loader}
              height="15px"
              width="98%"
            />

            <SkeletonLoader
              className={styles.loader}
              height="15px"
              width="93%"
            />

            <SkeletonLoader
              className={styles.loader}
              height="15px"
              width="96%"
            />
          </div>

          <div className={styles.bottomSection}>
            <SkeletonLoader
              className={styles.loader}
              height="54px"
              width="100%"
            />

            <SkeletonLoader
              className={styles.loader}
              height="54px"
              width="100%"
            />
          </div>
        </div>
      </ModuleWrapper>
    );

  if (hasNoData) return null;

  return (
    <ModuleWrapper
      ref={componentRef}
      className={styles.component}
      id={id}
      padding="none"
      settings={settings}
    >
      {pickupPoint && (
        <React.Fragment>
          <div
            className={classNames(styles.mainWrapper, {
              [styles.hasTag]:
                !!dataPickupPoint?.tag && dataPickupPoint?.tag.tagText !== '',
            })}
          >
            {dataPickupPoint?.tag && dataPickupPoint?.tag.tagText !== '' && (
              <Tag
                className={styles.tag}
                text={dataPickupPoint?.tag.tagText}
                variant={dataPickupPoint?.tag.tagType as TagVariant}
              />
            )}

            <div className={styles.topSection}>
              {pickupPoint.images && pickupPoint.images.length > 0 && (
                <div className={styles.imageWrapper}>
                  <ImageSlider
                    ignoreMinimumSize
                    images={pickupPoint.images.map(img => ({
                      altText: img.image.altText,
                      url: img.image.url,
                    }))}
                  />
                </div>
              )}

              <div className={styles.headlineWrapper}>
                <Typography className={styles.headline} tagStyle="headingSmall">
                  {pickupPoint.headline}
                </Typography>

                {shouldHighlightErrors && (
                  <Typography
                    className={styles.error}
                    text={`*${t('items.modules.bookingFlow.required')}`}
                  />
                )}
              </div>

              {pickupPoint.description && (
                <TruncatedText
                  onShowMoreClick={() => setShowMoreModal(true)}
                  richText={pickupPoint.description}
                  showMoreText={t('items.modules.bookingFlow.readMore')}
                />
              )}
            </div>

            {isMutatingAddPreBookingPickupPoint ||
            isMutatingDeletePreBookingPickupPoint ||
            (!isLoadingPickupPoint && isFetchingPickupPoint) ? (
              <div className={styles.bottomSection}>
                <SkeletonLoader
                  className={classNames(
                    styles.priceItemsWrapper,
                    styles.loader,
                  )}
                  height={isLaptop ? '54px' : '34px'}
                  width="100%"
                />

                <SkeletonLoader
                  className={classNames(
                    styles.selectedActionWrapper,
                    styles.loader,
                  )}
                  height="54px"
                  width="100%"
                />
              </div>
            ) : (
              <div className={styles.bottomSection}>
                <div className={styles.priceWrapper}>
                  <PriceItems priceItems={pickupPoint.priceItems} />

                  {(pickupPoint.state === 'fullSelected' ||
                    pickupPoint.state === 'partialSelected') && (
                    <Link className={styles.link} onClick={handleEdit}>
                      <Typography tagStyle="bodyMedium">
                        {t('items.modules.bookingFlow.edit')}
                      </Typography>
                    </Link>
                  )}
                </div>

                {(pickupPoint.state === 'fullSelected' ||
                  pickupPoint.state === 'partialSelected') && (
                  <div className={styles.selectedActionWrapper}>
                    {selectedPassengers.length > 0 && (
                      <div className={styles.passengersWrapper}>
                        <SelectedPassengers passengers={selectedPassengers} />
                      </div>
                    )}

                    <AlertPill
                      text={t('items.modules.bookingFlow.added')}
                      variant="success"
                    />
                  </div>
                )}

                {pickupPoint.state === 'unselected' && (
                  <div className={styles.unselectedActionWrapper}>
                    <Link
                      className={styles.link}
                      onClick={handleChooseIndividually}
                    >
                      <Typography tagStyle="bodyMedium">
                        {t('items.modules.bookingFlow.chooseIndividually')}
                      </Typography>
                    </Link>

                    {pickupPoint.choiceType === 'single' ? (
                      <Button
                        onClick={handleAddForAllSingleSelect}
                        tabIndex={-1}
                        text={t('items.modules.bookingFlow.addForAll')}
                        variant="secondary"
                      />
                    ) : (
                      <Select
                        label={t('items.modules.bookingFlow.select')}
                        onChange={handleAddForAllMultipleSelect}
                        options={multipleOptions}
                        required
                        value={selectedOption}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </React.Fragment>
      )}

      {pickupPoint && (
        <ExtraCardModal
          description={pickupPoint.description}
          headline={pickupPoint.headline}
          images={pickupPoint.images}
          isOpen={showMoreModal}
          onClose={() => setShowMoreModal(false)}
          priceItems={
            pickupPoint.priceItems
              ? pickupPoint.priceItems.map(item => ({
                  label: item.label ?? null,
                  price: item.price ?? null,
                  tooltip: null,
                }))
              : []
          }
        />
      )}

      {pickupPoint && (
        <PickupPointSelectModal
          choiceType={pickupPoint.choiceType}
          description={pickupPoint.overlayDescription}
          headline={pickupPoint.overlayHeadline}
          isOpen={showSelectModal}
          multipleOptions={multipleOptions}
          onClickSave={handleOnClickSave}
          onClose={() => setShowSelectModal(false)}
          passengers={pickupPoint.passengers}
          singleOption={singleOption}
        />
      )}
    </ModuleWrapper>
  );
};

PickupPoint.displayName = 'PickupPoint';

export default PickupPoint;
